import React, { useState, useEffect } from 'react';
import { AlertTriangle, Shield, X, Eye, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface ModerationNotification {
  id: string;
  type: string;
  message: string;
  read: boolean;
  createdAt: string;
  moderationData?: {
    reason: string;
    severity: string;
    strikeCount?: number;
    action: string;
  };
  sender?: {
    name: string;
    role: string;
  };
}

interface UserStrikes {
  strikeCount: number;
  strikeHistory: Array<{
    reason: string;
    severity: string;
    addedAt: string;
    notes: string;
  }>;
}

const ModerationNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<ModerationNotification[]>([]);
  const [strikes, setStrikes] = useState<UserStrikes>({ strikeCount: 0, strikeHistory: [] });
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    fetchModerationNotifications();
    fetchUserStrikes();
    
    // Check if user has hidden the alerts
    const hiddenState = localStorage.getItem('moderationAlertsHidden');
    if (hiddenState === 'true') {
      setIsHidden(true);
    }
  }, []);

  const fetchModerationNotifications = async () => {
    try {
      const response = await api.get('/user/notifications/moderation');
      if (response.data.success) {
        setNotifications(response.data.data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching moderation notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStrikes = async () => {
    try {
      const response = await api.get('/user/notifications/strikes');
      if (response.data.success) {
        setStrikes(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching user strikes:', error);
    }
  };

  const markAsRead = async (notificationIds: string[]) => {
    try {
      await api.put('/user/notifications/mark-read', { notificationIds });
      setNotifications(prev => 
        prev.map(notif => 
          notificationIds.includes(notif.id) 
            ? { ...notif, read: true }
            : notif
        )
      );
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'moderation_warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'moderation_strike':
        return <Shield className="w-5 h-5 text-red-600" />;
      case 'moderation_post_deleted':
        return <X className="w-5 h-5 text-red-600" />;
      case 'moderation_post_edited':
        return <Eye className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'minor':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'moderate':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'severe':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleHideAlerts = () => {
    setIsHidden(true);
    localStorage.setItem('moderationAlertsHidden', 'true');
    toast.success('Moderation alerts hidden. You can still view them in your profile settings.');
  };

  const handleShowAlerts = () => {
    setIsHidden(false);
    localStorage.setItem('moderationAlertsHidden', 'false');
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const displayNotifications = showAll ? notifications : notifications.slice(0, 3);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  // Don't show component if no moderation notifications
  if (notifications.length === 0 && strikes.strikeCount === 0) {
    return null;
  }

  // Show minimized version if hidden
  if (isHidden) {
    return (
      <div className="bg-gray-100 rounded-lg border border-gray-200 p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Moderation alerts hidden</span>
            {strikes.strikeCount > 0 && (
              <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-medium rounded-full">
                {strikes.strikeCount} strike{strikes.strikeCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={handleShowAlerts}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Show
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-gray-900">Moderation Alerts</h3>
            {unreadNotifications.length > 0 && (
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                {unreadNotifications.length} new
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleHideAlerts}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              title="Hide moderation alerts"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center space-x-4">
            {strikes.strikeCount > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Strikes:</span>
                <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                  strikes.strikeCount >= 3 
                    ? 'bg-red-100 text-red-800' 
                    : strikes.strikeCount >= 2 
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {strikes.strikeCount}
                </span>
              </div>
            )}
            <button
              onClick={handleHideAlerts}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              title="Hide moderation alerts"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Strike Warning */}
      {strikes.strikeCount > 0 && (
        <div className={`p-4 border-b border-gray-200 ${
          strikes.strikeCount >= 3 
            ? 'bg-red-50' 
            : strikes.strikeCount >= 2 
              ? 'bg-orange-50'
              : 'bg-yellow-50'
        }`}>
          <div className="flex items-start space-x-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 ${
              strikes.strikeCount >= 3 
                ? 'text-red-600' 
                : strikes.strikeCount >= 2 
                  ? 'text-orange-600'
                  : 'text-yellow-600'
            }`} />
            <div className="flex-1">
              <p className={`font-medium ${
                strikes.strikeCount >= 3 
                  ? 'text-red-800' 
                  : strikes.strikeCount >= 2 
                    ? 'text-orange-800'
                    : 'text-yellow-800'
              }`}>
                {strikes.strikeCount >= 3 
                  ? 'Account at Risk' 
                  : strikes.strikeCount >= 2 
                    ? 'Multiple Violations'
                    : 'Community Guidelines Violation'
                }
              </p>
              <p className={`text-sm ${
                strikes.strikeCount >= 3 
                  ? 'text-red-700' 
                  : strikes.strikeCount >= 2 
                    ? 'text-orange-700'
                    : 'text-yellow-700'
              }`}>
                {strikes.strikeCount >= 3 
                  ? 'Your account may be subject to restrictions. Please review our community guidelines.'
                  : strikes.strikeCount >= 2 
                    ? 'One more strike may result in account restrictions.'
                    : 'Please be mindful of our community guidelines to avoid further violations.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="divide-y divide-gray-100">
          {displayNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-4 ${!notification.read ? 'bg-blue-50' : ''}`}
              onClick={() => !notification.read && markAsRead([notification.id])}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                      {notification.type === 'moderation_warning' && 'Warning Received'}
                      {notification.type === 'moderation_strike' && 'Strike Added'}
                      {notification.type === 'moderation_post_deleted' && 'Post Removed'}
                      {notification.type === 'moderation_post_edited' && 'Post Edited'}
                    </p>
                    <div className="flex items-center space-x-2">
                      {notification.moderationData?.severity && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(notification.moderationData.severity)}`}>
                          {notification.moderationData.severity.toUpperCase()}
                        </span>
                      )}
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {notification.message}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      {formatDate(notification.createdAt)}
                    </p>
                    {notification.moderationData?.strikeCount && (
                      <span className="text-xs text-red-600 font-medium">
                        Total strikes: {notification.moderationData.strikeCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show More/Less */}
      {notifications.length > 3 && (
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {showAll ? 'Show Less' : `Show All (${notifications.length})`}
          </button>
        </div>
      )}

      {/* Mark All as Read */}
      {unreadNotifications.length > 0 && (
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => markAsRead(unreadNotifications.map(n => n.id))}
            className="text-sm text-gray-600 hover:text-gray-700"
          >
            Mark all as read
          </button>
        </div>
      )}
    </div>
  );
};

export default ModerationNotifications;